import { Link } from 'gatsby';
import * as React from 'react';
import { Popover, PopoverPosition, PopoverAlign } from 'react-tiny-popover';
import styled, { css } from 'styled-components';
import { pages } from '../../config';
import Button from './Button';
import MenuLink from './MenuLink';
import { PlainButton } from './styled-components';
import useKeyboardEventHandler from '../hooks/useKeyboardEventHandler';
import zIndexes from '../z-indexes';

const Menu = styled.div`
  min-width: 240px;
  padding: 10px;
  background: #fff;
  border-radius: 10px;
`;

const StyledMenuLink = styled(MenuLink)``;
const MenuButton = styled(PlainButton)``;

const MenuItem = styled.div<{ $withUpgradeNotice?: boolean }>`
  &:not(:first-of-type) {
    margin-top: 5px;
  }

  ${StyledMenuLink},
  ${MenuButton} {
    display: flex;
    width: 100%;
    min-height: 51px; // fix for height change on hover (due to font-weight change)
    padding: 0 15px;
    font-weight: 300;
    align-items: center;
    white-space: nowrap;
    text-decoration: none;
    border-radius: 5px;
    transition: background 0.2s ease-in;
    color: var(--earth-fm--color--green);

    &:hover,
    &:focus {
      font-weight: 600;
      background: var(--earth-fm--color--dark-beige-40);
    }

    &:disabled {
      font-weight: inherit;
      opacity: 0.5;
    }
  }

  ${({ $withUpgradeNotice }) =>
    $withUpgradeNotice
      ? css`
          display: flex;
          align-items: center;
          justify-content: space-between;
          white-space: nowrap;

          a {
            flex-shrink: 0;
            padding-left: 10px;
            padding-right: 10px;
          }

          @media (min-width: 375px) {
            gap: 10px;

            a {
              padding-left: 21px;
              padding-right: 21px;
            }
          }
        `
      : ''}
`;

const Header = styled.div`
  padding: 10px 15px;
`;

const Footer = styled.div`
  padding: 10px 15px;
`;

const Divider = styled.hr`
  margin: 10px 0;
  border: 0;
  border-top: 1px solid var(--earth-fm--color--green-15);
`;

export interface ActionMenuItem {
  id?: string;
  content: string;
  isDivider?: boolean;
  onClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  to?: string;
  target?: string;
  showUpgradeNotice?: boolean;
  hasPopup?: boolean;
}

interface Props {
  isOpen: boolean;
  items: ActionMenuItem[];
  className?: string;
  header?: React.ReactNode;
  footer?: React.ReactNode;
  positions?: PopoverPosition[];
  align?: PopoverAlign;
  children: React.ReactElement;
  onClose: () => void;
  parentElement?: HTMLElement;
}

export default function ActionMenu({
  isOpen,
  items,
  className = undefined,
  header = null,
  footer = null,
  positions = undefined,
  align = undefined,
  children,
  onClose,
  parentElement = undefined,
}: Props) {
  const actionMenuRef = React.useRef<HTMLDivElement>(null);

  const handleKeyUp = useKeyboardEventHandler('Escape', onClose);

  React.useEffect(() => {
    const closeActionMenuWithTab = (event: KeyboardEvent) => {
      if (event.key === 'Tab') {
        onClose();
      }
    };

    if (isOpen && actionMenuRef) {
      const lastMenuItem = actionMenuRef.current?.querySelector('div:last-child') as HTMLDivElement;

      if (lastMenuItem) {
        lastMenuItem.addEventListener('keydown', closeActionMenuWithTab);
      }

      return () => {
        lastMenuItem.removeEventListener('keydown', closeActionMenuWithTab);
      };
    }
    return () => {};
  }, [isOpen, onClose]);

  return (
    <Popover
      isOpen={isOpen}
      onClickOutside={() => onClose()}
      positions={positions}
      align={align}
      reposition
      boundaryElement={typeof document !== 'undefined' ? document.body : undefined}
      boundaryInset={10}
      containerStyle={{ zIndex: zIndexes.actionMenu.toString(), marginTop: '5px' }}
      parentElement={parentElement}
      content={
        <Menu className={className} ref={actionMenuRef} role="menu" onKeyUp={handleKeyUp}>
          {header ? (
            <>
              <Header>{header}</Header>
              <Divider />
            </>
          ) : null}
          {items.map(
            ({
              id,
              content,
              isDivider,
              onClick,
              to,
              target,
              showUpgradeNotice = false,
              hasPopup = false,
            }) => {
              if (isDivider) {
                return <Divider key={id || content} />;
              }
              if (onClick || showUpgradeNotice) {
                return (
                  <MenuItem
                    key={id || content}
                    $withUpgradeNotice={showUpgradeNotice}
                    role="presentation"
                  >
                    <MenuButton
                      onClick={onClick}
                      disabled={showUpgradeNotice}
                      role="menuitem"
                      aria-haspopup={hasPopup ? 'dialog' : false}
                    >
                      {content}
                    </MenuButton>

                    {showUpgradeNotice ? (
                      <Button secondary small as={Link} to={pages.becomeAMember} role="menuitem">
                        for members 🙏🏼
                      </Button>
                    ) : null}
                  </MenuItem>
                );
              }
              if (to) {
                return (
                  <MenuItem key={id || content} role="presentation">
                    <StyledMenuLink to={to} target={target} role="menuitem">
                      {content === 'Community' ? (
                        <>
                          {content}
                          <span className="screen-reader-text">open in a new tab</span>
                        </>
                      ) : (
                        content
                      )}
                    </StyledMenuLink>
                  </MenuItem>
                );
              }
              return null;
            },
          )}
          {footer ? (
            <>
              <Divider />
              <Footer>{footer}</Footer>
            </>
          ) : null}
        </Menu>
      }
    >
      {children}
    </Popover>
  );
}
